import React, { useCallback, useRef, useState } from 'react';
import { cn } from '../../utils/cn.ts';
import { Icon } from './Icon.tsx';

const NativeButton = 'button';
const SIDE_TRAY_DRAG_DISMISS_SELECTOR = '[data-side-tray-dismiss-on-drag="true"], [draggable="true"]';
const SIDE_TRAY_DRAG_DISMISS_THRESHOLD_PX = 8;

type SideTrayView = 'main' | 'side';
type SideDragPointer = { pointerId: number; startX: number; startY: number };

interface SideTrayLayoutProps {
    mainContent: React.ReactNode;
    sideContent: React.ReactNode;
    sideTitle: string;
    sideIcon?: string;
    sideButtonLabel?: string;
    className?: string;
    mainClassName?: string;
    mainInnerClassName?: string;
    mainToggleClassName?: string;
    sideClassName?: string;
    sidePanelClassName?: string;
    sideHeaderClassName?: string;
    sideBodyClassName?: string;
    dismissSideOnDragStart?: boolean;
    sideCollapsible?: boolean;
    defaultSideCollapsed?: boolean;
    sideCollapsed?: boolean;
    onSideCollapsedChange?: (collapsed: boolean) => void;
    view?: SideTrayView;
    onViewChange?: (view: SideTrayView) => void;
}

function useSideTrayDragDismiss({
    dismissSideOnDragStart,
    showMainView,
    view,
}: {
    dismissSideOnDragStart: boolean;
    showMainView: () => void;
    view: SideTrayView;
}) {
    const sideDragPointerRef = useRef<SideDragPointer | null>(null);

    const clearSideDragPointer = useCallback(() => {
        sideDragPointerRef.current = null;
    }, []);

    const handleSideDragStartCapture = useCallback(() => {
        if (dismissSideOnDragStart && view === 'side') {
            showMainView();
        }
    }, [dismissSideOnDragStart, showMainView, view]);

    const handleSidePointerDownCapture = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!dismissSideOnDragStart || view !== 'side') {
            return;
        }

        const target = event.target instanceof Element ? event.target : null;
        if (!target?.closest(SIDE_TRAY_DRAG_DISMISS_SELECTOR)) {
            return;
        }

        sideDragPointerRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
        };
    }, [dismissSideOnDragStart, view]);

    const handleSidePointerMoveCapture = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const pointer = sideDragPointerRef.current;
        if (!pointer || pointer.pointerId !== event.pointerId || view !== 'side') {
            return;
        }

        const distance = Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY);
        if (distance >= SIDE_TRAY_DRAG_DISMISS_THRESHOLD_PX) {
            clearSideDragPointer();
            showMainView();
        }
    }, [clearSideDragPointer, showMainView, view]);

    return {
        onDragStartCapture: handleSideDragStartCapture,
        onPointerCancelCapture: clearSideDragPointer,
        onPointerDownCapture: handleSidePointerDownCapture,
        onPointerMoveCapture: handleSidePointerMoveCapture,
        onPointerUpCapture: clearSideDragPointer,
    };
}

/**
 * A responsive layout primitive that shows main content and a side tray.
 * Desktop: Side-by-side layout.
 * Mobile: Toggleable view between main content and the side tray.
 */
export const SideTrayLayout: React.FC<SideTrayLayoutProps> = ({
    mainContent,
    sideContent,
    sideTitle,
    sideIcon = 'library_music',
    sideButtonLabel = 'Open',
    className,
    mainClassName,
    mainInnerClassName,
    mainToggleClassName,
    sideClassName,
    sidePanelClassName,
    sideHeaderClassName,
    sideBodyClassName,
    dismissSideOnDragStart = false,
    sideCollapsible = true,
    defaultSideCollapsed = false,
    sideCollapsed: controlledSideCollapsed,
    onSideCollapsedChange,
    view: controlledView,
    onViewChange
}) => {
    const [uncontrolledView, setUncontrolledView] = useState<SideTrayView>('main');
    const [uncontrolledSideCollapsed, setUncontrolledSideCollapsed] = useState(defaultSideCollapsed);
    const view = controlledView ?? uncontrolledView;
    const isSideCollapsedControlled = controlledSideCollapsed !== undefined;
    const sideCollapsed = sideCollapsible
        ? controlledSideCollapsed ?? uncontrolledSideCollapsed
        : false;

    const setView = useCallback((nextView: SideTrayView) => {
        if (controlledView === undefined) {
            setUncontrolledView(nextView);
        }
        onViewChange?.(nextView);
    }, [controlledView, onViewChange]);

    const setSideCollapsed = useCallback((collapsed: boolean) => {
        if (!isSideCollapsedControlled) {
            setUncontrolledSideCollapsed(collapsed);
        }
        onSideCollapsedChange?.(collapsed);
    }, [isSideCollapsedControlled, onSideCollapsedChange]);

    const canToggleSideCollapse = sideCollapsible && (
        !isSideCollapsedControlled || Boolean(onSideCollapsedChange)
    );

    const showMainView = useCallback(() => {
        setView('main');
    }, [setView]);

    const handleOpenSide = useCallback(() => {
        setSideCollapsed(false);
        setView('side');
    }, [setSideCollapsed, setView]);

    const handleCollapseSide = useCallback(() => {
        setSideCollapsed(true);
    }, [setSideCollapsed]);

    const sideDragDismissHandlers = useSideTrayDragDismiss({
        dismissSideOnDragStart,
        showMainView,
        view,
    });

    return (
        <div className={cn('flex h-full flex-col gap-4 overflow-hidden p-4 lg:flex-row lg:gap-6 lg:p-6', className)}>
            {/* Main Area */}
            <div
                className={cn(
                    view === 'main' ? 'flex' : 'hidden',
                    'h-full min-h-0 min-w-0 flex-1 lg:flex',
                    mainClassName
                )}
            >
                <div className={cn('flex h-full min-h-0 w-full flex-col gap-4', mainInnerClassName)}>
                    {/* Mobile Toggle Button */}
                    <div className="flex justify-end lg:hidden">
                        <NativeButton
                            type="button"
                            onClick={handleOpenSide}
                            className={cn(
                                'flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-bold text-primary-content text-xs uppercase tracking-wider shadow-lg',
                                mainToggleClassName
                            )}
                        >
                            <Icon name={sideIcon} size="sm" />
                            {sideButtonLabel}
                        </NativeButton>
                    </div>
                    {mainContent}
                </div>
            </div>

            {/* Sidebar (Tray) */}
            <div
                data-side-tray
                className={cn(
                    view === 'side' ? 'flex' : 'hidden',
                    'h-full min-h-0 w-full min-w-0 shrink-0 flex-col lg:flex lg:w-80',
                    sideCollapsed && 'lg:hidden',
                    sideClassName
                )}
                {...sideDragDismissHandlers}
            >
                <div
                    className={cn(
                        'flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-border-dark dark:bg-surface-dark',
                        sidePanelClassName
                    )}
                >
                    {/* Header */}
                    <div
                        className={cn(
                            'flex items-center justify-between border-gray-200 border-b bg-gray-50 p-4 dark:border-border-dark dark:bg-background-dark',
                            sideHeaderClassName
                        )}
                    >
                        <h3 className="font-bold text-gray-900 text-sm uppercase tracking-tight dark:text-gray-100">{sideTitle}</h3>
                        {canToggleSideCollapse ? (
                            <NativeButton
                               type="button"
                               aria-label={`Collapse ${sideTitle}`}
                               className="hidden h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary lg:inline-flex dark:text-gray-300 dark:hover:bg-surface-muted-dark dark:hover:text-gray-100"
                               onClick={handleCollapseSide}
                            >
                               <Icon name="right_panel_close" size="sm" />
                            </NativeButton>
                        ) : null}
                        <NativeButton
                           type="button"
                           className="font-bold text-primary text-sm uppercase tracking-wider lg:hidden"
                           onClick={() => setView('main')}
                        >
                           Done
                        </NativeButton>
                    </div>
                    <div className={cn('flex-1 overflow-hidden', sideBodyClassName)}>
                        {sideContent}
                    </div>
                </div>
            </div>

            {sideCollapsed && canToggleSideCollapse ? (
                <div className="hidden h-full w-12 shrink-0 items-stretch lg:flex">
                    <NativeButton
                        type="button"
                        aria-label={`Open ${sideTitle}`}
                        className="flex h-full w-full items-start justify-center rounded-xl border border-gray-200 bg-white pt-4 text-gray-600 shadow-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-dark dark:bg-surface-dark dark:text-gray-300 dark:hover:bg-surface-muted-dark dark:hover:text-gray-100"
                        onClick={handleOpenSide}
                    >
                        <Icon name="right_panel_open" size="sm" />
                    </NativeButton>
                </div>
            ) : null}
        </div>
    );
};











